'use client';

import React, { useState } from 'react';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import ToastStack from './ToastStack';
import ActivityLogPanel from './ActivityLogPanel';
import AuthGuard from './AuthGuard';
import AppIcon from '@/components/ui/AppIcon';
import { useNotifications } from '@/lib/notificationContext';
import { useAuth } from '@/lib/authContext';
import { useRouter } from 'next/navigation';

interface AppLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
}

const ROLE_COLORS: Record<string, string> = {
  Admin: 'bg-purple-100 text-purple-700',
  'Sales Manager': 'bg-blue-100 text-blue-700',
  'Inventory Manager': 'bg-teal-100 text-teal-700',
  Merchandiser: 'bg-orange-100 text-orange-700',
  Supervisor: 'bg-indigo-100 text-indigo-700',
};

const AVATAR_BG: Record<string, string> = {
  Admin: 'bg-purple-500',
  'Sales Manager': 'bg-blue-500',
  'Inventory Manager': 'bg-teal-500',
  Merchandiser: 'bg-orange-500',
  Supervisor: 'bg-indigo-500',
};

function NotificationBell() {
  const { unreadCount, setShowLogPanel } = useNotifications();
  return (
    <button
      onClick={() => setShowLogPanel(true)}
      className="relative flex items-center justify-center w-9 h-9 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
      title="Activity Log"
    >
      <AppIcon name="BellIcon" size={20} />
      {unreadCount > 0 && (
        <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-primary text-white text-[10px] font-700 flex items-center justify-center leading-none">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const router = useRouter();
  const [open, setOpen] = useState(false);

  if (!user) return null;

  const handleLogout = () => {
    logout();
    router.replace('/login');
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted transition-colors"
        title={user.name}
      >
        <span className={`w-7 h-7 rounded-full ${AVATAR_BG[user.role] ?? 'bg-gray-500'} text-white text-xs font-700 flex items-center justify-center shrink-0`}>
          {user.avatar}
        </span>
        <div className="hidden sm:flex flex-col items-start leading-tight">
          <span className="text-xs font-600 text-foreground max-w-[100px] truncate">{user.name.split(' ')[0]}</span>
          <span className={`text-[10px] font-500 px-1.5 rounded-full ${ROLE_COLORS[user.role] ?? 'bg-gray-100 text-gray-600'}`}>{user.role}</span>
        </div>
        <AppIcon name="ChevronDownIcon" size={12} className="text-muted-foreground hidden sm:block" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 w-56 bg-card border border-border rounded-xl shadow-lg z-50 overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <p className="text-sm font-600 text-foreground">{user.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              <span className={`mt-1 inline-block text-[10px] font-500 px-2 py-0.5 rounded-full ${ROLE_COLORS[user.role] ?? 'bg-gray-100 text-gray-600'}`}>{user.role}</span>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <AppIcon name="ArrowRightOnRectangleIcon" size={16} />
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function AppLayout({ children, title, subtitle }: AppLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <AuthGuard>
      <div className="min-h-screen bg-background flex">
        {/* Sidebar */}
        <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />

        {/* Main content */}
        <div
          className="flex-1 flex flex-col min-w-0 sidebar-transition"
          style={{ marginLeft: collapsed ? '64px' : '240px' }}
        >
          <div className="flex items-center">
            <div className="flex-1">
              <Topbar title={title ?? ''} subtitle={subtitle} />
            </div>
            <div className="pr-4 shrink-0 flex items-center gap-1">
              <NotificationBell />
              <UserMenu />
            </div>
          </div>
          <main className="flex-1 overflow-auto">
            {children}
          </main>
        </div>

        {/* Global overlays */}
        <ActivityLogPanel />
        <ToastStack />
      </div>
    </AuthGuard>
  );
}